/**
 * The open trace (1.X deliverable 11; DECISIONS 168 and 186): User Timing marks
 * `codex.open.<phase>` along a document's open, and `openPhases`, which turns
 * them into the time each phase waited.
 *
 * The marks, in the order the open passes them: the blocker's start (leaving
 * the previous document), `leave`'s end, the route's `beforeLoad`, `auth.me`'s
 * answer, `DocPage`'s mount, `docs.get`'s answer, the session opened,
 * `DocEditor`'s first commit and `onReady`. The blocker's start clears the
 * previous open's marks and measures first. A fresh load has no blocker, so its
 * trace starts at `beforeLoad`.
 *
 * Pure apart from `markOpen` and `clearOpenTrace`, which only write User Timing
 * entries (not a secure-context API; plain HTTP keeps it). Nothing here reads a
 * body, a title or any document text.
 */

/** The phases in the order an open passes them. */
export const OPEN_PHASES = [
  'blocker',
  'leave',
  'before-load',
  'auth-me',
  'mount',
  'docs-get',
  'session',
  'commit',
  'ready',
] as const;
export type OpenPhase = (typeof OPEN_PHASES)[number];

export const OPEN_MARK_PREFIX = 'codex.open.';

export const openMarkName = (phase: OpenPhase): string => `${OPEN_MARK_PREFIX}${phase}`;

/** A mark as User Timing hands it back (`PerformanceMark` is one). */
export interface OpenMark {
  readonly name: string;
  readonly startTime: number;
}

export interface OpenPhases {
  /**
   * Per phase present: the time from the latest earlier phase (in the open's
   * order) that had already happened when it did. With the document fetched
   * beside the session check, `docs-get` may answer before `auth-me` or
   * `mount`; it then counts from the latest one before it, so no gap is
   * negative and each names what that phase waited for.
   */
  readonly gaps: Partial<Record<OpenPhase, number>>;
  /** From the first mark present to the last. */
  readonly total: number;
}

const isPhase = (name: string): name is OpenPhase =>
  (OPEN_PHASES as readonly string[]).includes(name);

/** The gaps of one open's marks (the newest mark of each phase wins). */
export function openPhases(marks: readonly OpenMark[]): OpenPhases {
  const at = new Map<OpenPhase, number>();
  for (const mark of marks) {
    if (!mark.name.startsWith(OPEN_MARK_PREFIX)) continue;
    const phase = mark.name.slice(OPEN_MARK_PREFIX.length);
    if (!isPhase(phase) || !Number.isFinite(mark.startTime)) continue;
    at.set(phase, mark.startTime);
  }
  const gaps: Partial<Record<OpenPhase, number>> = {};
  const seen: number[] = [];
  for (const phase of OPEN_PHASES) {
    const time = at.get(phase);
    if (time === undefined) continue;
    const before = seen.filter((t) => t <= time);
    gaps[phase] = before.length === 0 ? 0 : time - Math.max(...before);
    seen.push(time);
  }
  const times = [...at.values()];
  const total = times.length === 0 ? 0 : Math.max(...times) - Math.min(...times);
  return { gaps, total };
}

const timing = (): Performance | null =>
  typeof performance !== 'undefined' && typeof performance.mark === 'function' ? performance : null;

/** Marks one phase of the current open. */
export function markOpen(phase: OpenPhase): void {
  try {
    timing()?.mark(openMarkName(phase));
  } catch {
    // User Timing is a measurement aid only; an open never fails on it.
  }
}

/** Clears the previous open's marks and measures (the blocker's start does it first). */
export function clearOpenTrace(): void {
  const perf = timing();
  if (!perf) return;
  try {
    for (const phase of OPEN_PHASES) {
      perf.clearMarks(openMarkName(phase));
      perf.clearMeasures(openMarkName(phase));
    }
  } catch {
    // As above.
  }
}

/** The current open's marks, as User Timing holds them. */
export function openMarks(): OpenMark[] {
  const perf = timing();
  if (!perf || typeof perf.getEntriesByType !== 'function') return [];
  return perf
    .getEntriesByType('mark')
    .filter((entry) => entry.name.startsWith(OPEN_MARK_PREFIX))
    .map((entry) => ({ name: entry.name, startTime: entry.startTime }));
}
